import * as _ from 'lodash-es'
import { defineStore } from 'pinia'

import { TableWithReservationAndUpdates } from '@/common/interfaces/TableBulkEditing'
import { isWalkIn } from '@/models'
import { useCSDateStore } from '@/modules/csDate/stores/CSDateStore'
import {
  PerformanceTableEvent,
  TABLE_UPDATE_STATUS,
} from '@/modules/performance/interfaces/PerformanceTableInterfaces'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useReservationStore } from '@/modules/reservation/stores/ReservationStore'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import { sum } from '@/utils/utils'

import {
  CSDateId,
  CSDateReservationsRead,
  GuestRead,
  PerformanceId,
  PerformanceReservationsRead,
  PerformanceTableId,
  PerformanceTableRead,
  ReservationDetailedRead,
  ReservationId,
  ReservationRead,
  ReservationStatus,
} from '@generated/types'

export type EventIdParam = `p_${number}` | `c_${number}`
type SelectedEvent = PerformanceReservationsRead | CSDateReservationsRead | null
type SelectedEventId = PerformanceId | CSDateId

export interface PerformanceReservationState {
  selectedEventId: SelectedEventId
  selectedEvent: SelectedEvent
  loadingState: PerformanceLoadingStatus
  selectedTableIds: PerformanceTableId[]
  lowOpacityTableIds: PerformanceTableId[]
  filledTableIds: PerformanceTableId[]
  selectedReservation: ReservationDetailedRead | null
  loadingReservationState: ReservationLoadingStatus
  isPromoteDrawerOpen: boolean
}
type PerformanceLoadingStatus =
  (typeof PERFORMANCE_LOADING_STATE)[keyof typeof PERFORMANCE_LOADING_STATE]
type ReservationLoadingStatus =
  (typeof RESERVATION_LOADING_STATE)[keyof typeof RESERVATION_LOADING_STATE]
export type PerformanceTableWithReservations = TableWithReservationAndUpdates<PerformanceTableRead>
export const PERFORMANCE_LOADING_STATE = {
  NONE: 'none',
  LOADING: 'loading',
}
export const RESERVATION_LOADING_STATE = _.cloneDeep(PERFORMANCE_LOADING_STATE)
export const PERFORMANCE_PARAMS = { EMPTY: 0 }

export const usePerformanceReservationStore = defineStore({
  id: 'performanceReservation',
  state: (): PerformanceReservationState => ({
    selectedEventId: PERFORMANCE_PARAMS.EMPTY,
    selectedEvent: null,
    loadingState: PERFORMANCE_LOADING_STATE.NONE,
    selectedTableIds: [],
    lowOpacityTableIds: [],
    filledTableIds: [],
    selectedReservation: null,
    loadingReservationState: RESERVATION_LOADING_STATE.NONE,
    isPromoteDrawerOpen: false,
  }),
  actions: {
    isPerformance(event: any): event is PerformanceReservationsRead {
      return !!event && 'show_id' in event
    },
    isCSDate(event: any): event is CSDateReservationsRead {
      return !!event && 'csarea_id' in event
    },
    getPerformanceIdValue(performanceId: EventIdParam | number): SelectedEventId {
      if (typeof performanceId === 'number') return performanceId
      const [, value] = performanceId.split('_')
      if (isNaN(Number(value))) return PERFORMANCE_PARAMS.EMPTY
      return Number(value)
    },
    getPerformanceIdParam(id: number): EventIdParam {
      const performanceStore = usePerformanceStore()
      const csDateStore = useCSDateStore()
      const performance = performanceStore.performances.find(performance => performance.id === id)
      if (performance) return `p_${performance.id}`
      const csDate = csDateStore.csDates.find(csDate => csDate.id === id)
      if (csDate) return `c_${csDate.id}`
      return `p_${PERFORMANCE_PARAMS.EMPTY}`
    },
    async backgroundRefresh(): Promise<void> {
      if (!this.selectedEvent) return
      const performanceStore = usePerformanceStore()
      const csDateStore = useCSDateStore()
      if (this.isCSDate(this.selectedEvent)) {
        this.setSelectedPerformance(
          await csDateStore.getCSDateReservationsById(this.selectedEvent.id),
        )
      } else {
        this.setSelectedPerformance(
          await performanceStore.getPerformanceReservations(this.selectedEvent.id),
        )
      }
    },
    setSelectedPerformance(
      performance: Partial<PerformanceReservationsRead | CSDateReservationsRead>,
    ): void {
      this.selectedEvent = _.cloneDeep({
        ...this.selectedEvent,
        ...performance,
      }) as PerformanceReservationsRead | CSDateReservationsRead
      if (!this.selectedEvent || this.isCSDate(this.selectedEvent)) return

      const settingStore = useSettingStore()
      if (
        this.selectedEvent.on_sale_datetime &&
        settingStore.parseMoment(this.selectedEvent.on_sale_datetime).isBefore('2021-01-01')
      ) {
        this.selectedEvent.on_sale_datetime = null
      }
      this.selectedEvent.tables.forEach(table => {
        if (!table.type_id) table.type_id = settingStore.table_types[0].id
      })
      // Sort seating times by time
      this.selectedEvent.seating_times = _.sortBy(this.selectedEvent.seating_times, 'time')
      // This is because we get numbers like "8.2500" from the API, and when we put the price in the form,
      // it is parsed to "8.25", and it shows the user the interface to save changes as if the price was changed.
      this.selectedEvent.add_ons.forEach(addOn => {
        addOn.price = Number(addOn.price).toFixed(2)
        addOn.tax_rate = Number(addOn.tax_rate).toFixed(2)
      })
      this.selectedEvent.service_fee =
        this.selectedEvent.service_fee === null ? null : Number(this.selectedEvent.service_fee)
      this.selectedEvent.service_percentage =
        this.selectedEvent.service_percentage === null
          ? null
          : Number(this.selectedEvent.service_percentage)
    },
    async loadSelectedEvent(eventId: EventIdParam | number): Promise<void> {
      try {
        const performanceStore = usePerformanceStore()
        const csDateStore = useCSDateStore()
        this.removeSelectedEvent()
        this.removeSelectedReservation()
        const eventIdValue = this.getPerformanceIdValue(eventId)
        if (eventIdValue === PERFORMANCE_PARAMS.EMPTY) return
        this.selectedEventId = eventIdValue
        this.loadingState = PERFORMANCE_LOADING_STATE.LOADING
        this.setSelectedPerformance(
          typeof eventId === 'number' || eventId.startsWith('p_')
            ? await performanceStore.getPerformanceReservations(eventIdValue)
            : await csDateStore.getCSDateReservationsById(eventIdValue),
        )
      } finally {
        this.loadingState = PERFORMANCE_LOADING_STATE.NONE
      }
    },
    removeSelectedEvent(): void {
      this.selectedEvent = null
    },
    setSelectedReservation(reservation: ReservationDetailedRead): void {
      this.selectedReservation = _.cloneDeep({ ...this.selectedReservation, ...reservation })
      this.setSelectedTableIds(this.selectedReservation.tables)
    },
    async loadSelectedReservation(reservationId: ReservationId): Promise<void> {
      try {
        const reservationStore = useReservationStore()
        this.loadingReservationState = RESERVATION_LOADING_STATE.LOADING
        this.selectedReservation = (await reservationStore.getReservation(
          reservationId,
        )) as unknown as ReservationDetailedRead
        this.setSelectedTableIds(this.selectedReservation.tables)
      } finally {
        this.loadingReservationState = RESERVATION_LOADING_STATE.NONE
      }
    },
    removeSelectedReservation(): void {
      this.selectedReservation = null
      this.setSelectedTableIds([])
    },
    setSelectedTableIds(tableIds: PerformanceTableId[]): void {
      this.selectedTableIds = tableIds.slice()
    },
    setLowOpacityTableIds(tableIds: PerformanceTableId[]): void {
      this.lowOpacityTableIds = tableIds.slice()
    },
    setFilledTableIds(tableIds: PerformanceTableId[]): void {
      this.filledTableIds = tableIds.slice()
    },
    toggleTableId(tableId: PerformanceTableId): void {
      const index = this.selectedTableIds.indexOf(tableId)
      if (index === -1) {
        this.selectedTableIds.push(tableId)
      } else {
        this.selectedTableIds.splice(index, 1)
      }
    },
    walkInTable(tableEvent: PerformanceTableEvent): void {
      const table = this.tableById(tableEvent.tableId)
      if (tableEvent.activeReservationId !== null && !table!.standing_room) return
      this.toggleTableId(tableEvent.tableId)
    },
    toggleReservationTable(tableEvent: PerformanceTableEvent): void {
      if (
        tableEvent.activeReservationId === null ||
        tableEvent.activeReservationId === this.selectedReservation?.id ||
        tableEvent.isStandingRoom ||
        (this.selectedReservation &&
          _.includes(this.selectedReservation.tables, tableEvent.tableId))
      ) {
        this.toggleTableId(tableEvent.tableId)
      }
    },
    onGuestChange(guest: GuestRead) {
      if (!this.selectedEvent) return
      this.selectedEvent.reservations.forEach(reservation => {
        if (reservation.contact?.id === guest.id) {
          Object.assign(reservation.contact, guest)
        }
      })
    },
    tableById(tableId: PerformanceTableId): PerformanceTableRead | undefined {
      return _.find(this.selectedEvent!.tables, { id: tableId })
    },
  },
  getters: {
    isLoadingSelectedPerformance(): boolean {
      return this.loadingState === PERFORMANCE_LOADING_STATE.LOADING
    },
    isLoadingSelectedReservation(): boolean {
      return this.loadingReservationState === RESERVATION_LOADING_STATE.LOADING
    },
    hasSelectedTables(): boolean {
      return this.selectedTables.length > 0
    },
    performanceReservations(): ReservationRead[] {
      if (!this.selectedEvent) return []
      const userStore = useUserStore()
      return this.selectedEvent.reservations.map(reservation => {
        const user = reservation.contact || userStore.userById(reservation.user)
        return { ...reservation, user }
      })
    },
    activeReservations(): ReservationRead[] {
      return _.reject(this.performanceReservations, reservation => {
        return (
          reservation.status === ReservationStatus.CANCELED ||
          reservation.status === ReservationStatus.CLEARED ||
          reservation.status === ReservationStatus.OBSOLETE
        )
      })
    },
    totalOfActiveEmails(): number {
      return _.uniq(this.activeReservations.map(r => r.user?.email).filter(Boolean)).length
    },
    inactiveReservations(): ReservationRead[] {
      return _.filter(this.performanceReservations, reservation => {
        return (
          reservation.status === ReservationStatus.CANCELED ||
          reservation.status === ReservationStatus.CLEARED
        )
      })
    },
    dashboardInformation() {
      const numberOfGuests = sum(this.activeReservations.map(r => r.number_of_guests))
      const numberOfWalkIns = sum(
        this.activeReservations.filter(isWalkIn).map(r => r.number_of_guests),
      )
      const numberOfHeldTables = this.selectedEvent?.tables.filter(table => table.held).length || 0
      return {
        reservations: this.activeReservations,
        numberOfGuests,
        numberOfWalkIns,
        numberOfHeldTables,
      }
    },
    tablesWithReservation(): PerformanceTableWithReservations[] {
      if (!this.selectedEvent) return []
      return this.selectedEvent.tables.map(table => {
        const activeReservation = _.find(this.activeReservations, reservation => {
          return _.includes(reservation.tables, table.id)
        })
        return {
          ...table,
          updateStatus: TABLE_UPDATE_STATUS.AVAILABLE,
          reservationStatus: activeReservation
            ? activeReservation.status
            : TABLE_UPDATE_STATUS.AVAILABLE,
          activeReservation: activeReservation || null,
          inactiveReservations: _.filter(this.inactiveReservations, reservation => {
            return _.includes(reservation.tables, table.id)
          }),
          hidden: false,
          allowToDelete: false,
          hasUpdates: false,
        }
      })
    },
    activeReservationTableIds(): PerformanceTableId[] {
      return this.tablesWithReservation
        .filter(table => Boolean(table.activeReservation))
        .map(table => table.id)
    },
    tableIdsWithNoReservation(): PerformanceTableId[] {
      return this.tablesWithReservation
        .filter(table => !table.activeReservation)
        .map(table => table.id)
    },
    selectedTables(): PerformanceTableWithReservations[] {
      return this.tablesWithReservation.filter(t => this.selectedTableIds.includes(t.id))
    },
    selectedTablesName(): string {
      return this.selectedTables.map(table => table.name + (table.held ? ' (Held)' : '')).join(', ')
    },
    bookableTableIds(): PerformanceTableId[] {
      if (!this.selectedEvent || !this.selectedReservation) return []
      return _.concat(
        this.tableIdsWithNoReservation,
        this.tablesWithReservation.filter(t => t.standing_room).map(t => t.id),
        this.selectedReservation.tables || [],
      )
    },
    nonBookableTableIds(): PerformanceTableId[] {
      if (!this.selectedEvent || !this.selectedReservation) return []
      return _.difference(
        this.selectedEvent.tables.map(t => t.id),
        this.bookableTableIds,
      )
    },
  },
})
